import classnames from 'classnames';
import { isEmpty, map } from 'ramda';
import { noop } from 'ramda-adjunct';
import React, { FC, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import { slug } from 'to-case';
import { TextButton, WixIcons, Text } from '../../../../../../components/common';
import { GalleryLayout } from '../../../../../../components/layouts/Gallery';
import { GalleryFile } from '../../../../../../components/layouts/Gallery/GalleryFile';
import { GalleryFolder } from '../../../../../../components/layouts/Gallery/GalleryFolder';
import { MediaManagerSDKAnyItem, MediaManagerSDKFile } from '../../../../../../utils/media-manager-sdk';
import { isFolder } from '../../../../../../utils/media-manager-sdk/items';
import { IOuter } from './Section.types';
import { useVisibleItems } from './useVisibleItems';
import styles from './Section.scss';

export const Section: FC<IOuter> = ({
  title,
  action: actionFromProps,
  isSecure,
  publicMediaCurrentCategory,
  isItemSelected,
  selectedItems,
  onItemClick,
  beginDrag,
  onFolderDoubleClick,
  onFileDoubleClick,
  openExpandedView,
  onItemRightClick,
  buyItem,
  alwaysShowAction,
  items,
  maxRowsToShow,
  onShowAllClick = noop,
  sectionName,
  currentFolder,
}) => {
  const action = actionFromProps || {};

  const {
    visibleItemsCount: _visibleItemsCount,
    shouldShowAllItems,
    visibleItems,
    showAllItems,
    itemsListRef,
  } = useVisibleItems({ items, maxRowsToShow });

  const history = useHistory();
  const handleShowAllClick = useCallback(() => {
    onShowAllClick();

    if (action && action.url) {
      history.push(action.url);
    } else {
      showAllItems();
    }
  }, [onShowAllClick, sectionName, action.url]);

  const visibleItemsCount = shouldShowAllItems ? items.length : _visibleItemsCount;
  const hasHiddenItems = items.length > visibleItemsCount;
  const shouldShowAllButton = hasHiddenItems || (alwaysShowAction && !isEmpty(action));

  return (
    <div className={styles.section} data-hook="section" ref={itemsListRef}>
      <Text weight="thin" size="medium" tagName="div" dataHook="section-title" className={styles.title}>
        {title}
      </Text>

      <GalleryLayout itemsCount={visibleItems.length} itemsClassName={styles.galleryLayoutItems}>
        {({ itemClassName }) => [
          ...map((item: MediaManagerSDKAnyItem) => {
            const isSelected = isItemSelected(item.id);

            return isFolder(item) ? (
              <GalleryFolder
                className={itemClassName}
                key={item.id}
                item={item}
                selectedItems={isSelected ? selectedItems : null}
                isSecure={isSecure}
                selected={isSelected}
                isCut={false}
                isEditing={false}
                onClick={onItemClick}
                onContextMenu={onItemRightClick}
                onNameChange={noop}
                onRenameStart={noop}
                onRenameComplete={noop}
                onRenameCancel={noop}
                openExpandedView={openExpandedView}
                beginDrag={beginDrag}
                onDoubleClick={onFolderDoubleClick}
                currentFolder={currentFolder}
              />
            ) : (
              <GalleryFile
                className={itemClassName}
                key={item.id}
                item={item as MediaManagerSDKFile}
                publicMediaCurrentCategory={publicMediaCurrentCategory}
                selectedItems={isSelected ? selectedItems : null}
                isSecure={isSecure}
                selected={isSelected}
                isCut={false}
                isEditing={false}
                onClick={onItemClick}
                onContextMenu={onItemRightClick}
                onNameChange={noop}
                onRenameStart={noop}
                onRenameComplete={noop}
                onRenameCancel={noop}
                openExpandedView={openExpandedView}
                onDoubleClick={onFileDoubleClick}
                buyItem={buyItem}
                beginDrag={beginDrag}
                currentFolder={currentFolder}
              />
            );
          }, visibleItems),

          shouldShowAllButton && (
            <li
              key="showAll"
              data-hook={`show-all-${slug(title.toString())}`}
              className={styles.showAll}
              onClick={handleShowAllClick}
            >
              <span
                data-hook="show-all"
                className={classnames(styles.icon, { [styles.iconWithUrl]: Boolean(action.url) })}
              >
                {action.url ? <WixIcons.ChevronRight /> : <WixIcons.Add />}
              </span>

              <TextButton as="span" weight="normal" className={styles.action}>
                {action.title || <FormattedMessage id="files-manager.section.show-all" />}
              </TextButton>
            </li>
          ),
        ]}
      </GalleryLayout>

      <div className={styles.divider} />
    </div>
  );
};
